import {
  DOCUMENT_VIEWER_LIST_ITEM_TEST_ID,
  DOCUMENT_VIEWER_LIST_TEST_ID,
} from '@ctw/shared/test/ids';
import { filterChildNodesByNodeName, uniqueIdentifierForNode } from '@ctw/shared/utils/cda/helpers';
import type { CdaParsedHtmlElementNode } from '@ctw/shared/utils/cda/types';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { memo, useMemo } from 'react';
import { ReactNodeRenderer } from './react-node-renderer';

interface ListRendererProps {
  id: string;
  list: CdaParsedHtmlElementNode;
}

export const ReactListRenderer = memo(({ id, list }: ListRendererProps) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally under-specific deps
  const listType = useMemo(
    () =>
      list.attributes.listType === 'ordered' || list.nodeName === 'ol' ? 'ordered' : 'unordered',
    [id],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally under-specific deps
  const children = useMemo(
    () => filterChildNodesByNodeName(list, ['item', 'li', 'ul', 'ol', 'list'], false),
    [id],
  );

  const listIdentifier = uniqueIdentifierForNode({
    node: list,
    context: id,
    purpose: 'semantic-list',
  });

  if (listType === 'ordered') {
    return (
      <ol
        key={listIdentifier}
        id={listIdentifier}
        className={twx('list-decimal pl-6', list.styles.className)}
        data-testid={DOCUMENT_VIEWER_LIST_TEST_ID}
      >
        {children.map((child, index) => {
          const listItemIdentifier = uniqueIdentifierForNode({
            node: child,
            context: listIdentifier,
            purpose: 'semantic-list-item',
            index,
          });

          if (['ul', 'ol', 'list'].includes(child.nodeName)) {
            return (
              <ReactListRenderer key={listItemIdentifier} id={listItemIdentifier} list={child} />
            );
          }

          return <ListItemRenderer key={listItemIdentifier} id={listItemIdentifier} item={child} />;
        })}
      </ol>
    );
  }

  // Default to unordered list
  return (
    <ul
      key={listIdentifier}
      id={listIdentifier}
      className={tw`list-disc pl-6`}
      data-testid={DOCUMENT_VIEWER_LIST_TEST_ID}
    >
      {children.map((child, index) => {
        const listItemIdentifier = uniqueIdentifierForNode({
          node: child,
          context: listIdentifier,
          purpose: 'semantic-list-item',
          index,
        });

        if (['ul', 'ol', 'list'].includes(child.nodeName)) {
          return (
            <ReactListRenderer key={listItemIdentifier} id={listItemIdentifier} list={child} />
          );
        }

        return <ListItemRenderer key={listItemIdentifier} id={listItemIdentifier} item={child} />;
      })}
    </ul>
  );
});

interface ListItemRendererProps {
  id: string;
  item: CdaParsedHtmlElementNode;
}

const ListItemRenderer = memo(({ id, item }: ListItemRendererProps) => {
  return (
    <li
      className={twx('text-background-inverse [&>*:only-of-type]:p-0', item.styles.className)}
      data-testid={DOCUMENT_VIEWER_LIST_ITEM_TEST_ID}
    >
      {item.childNodes.map((child, index) => {
        const listItemChildIdentifier = uniqueIdentifierForNode({
          node: child,
          context: id,
          purpose: 'semantic-list-item-child',
          index,
        });

        return (
          <ReactNodeRenderer
            key={listItemChildIdentifier}
            id={listItemChildIdentifier}
            node={child}
          />
        );
      })}
    </li>
  );
});
